import { graphql } from 'gatsby';
import {
	GatsbyImage,
	StaticImage,
	getImage,
	getSrc,
} from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';
import {
	ClockHistory,
	GeoAlt,
	HouseGear,
	Telephone,
} from 'react-bootstrap-icons';

import Framework from '../components/framework';
import Breadcrumb from '../components/framework/breadcrumb';
import Btn from '../components/framework/buttons/btn';
import Link from '../components/framework/link';
import PageHeader from '../components/framework/pages/page-header';
import SectionHeader from '../components/framework/sections/section-header';
import Slider from '../components/framework/slider';
import BookingBadge from '../components/location/booking_badge';
import LocationMap from '../components/location/map';
import LocationMarkers from '../components/location/map_markers';
import Offers from '../components/offers/offers';
import ProductList from '../components/test/product-list';
import Trans from '../components/translation/trans';
import { MapProvider } from '../context/map';
import useSiteMetadata from '../hooks/use_site_metadata';
import useTranslation from '../hooks/use_translation';
import { isSummerTime } from '../utils/date';
import { getVariantByLanguage } from '../utils/helpers';

const LocationItem = ({ data, location }) => {
	const { language, t } = useTranslation('template_location');
	const location_data = data.newLocation;

	const location_variant = getVariantByLanguage(
		language,
		location_data.location_variants,
	);

	const siteMetadata = useSiteMetadata();

	const facility_parking = location_data?.location_settings?.facility_parking;
	const facility_wheelchair_access =
		location_data?.location_settings?.facility_wheelchair_access;
	const image_header = location_data?.fields?.files_header_url_src;
	const lunch_hours = {
		monday: t('t3'),
		saturday: t('t17'),
	};
	const opening_hours = [
		{
			id: 'monday',
			text: t('t10'),
		},
		{
			id: 'tuesday',
			text: t('t11'),
		},
		{
			id: 'wednesday',
			text: t('t12'),
		},
		{
			id: 'thursday',
			text: t('t13'),
		},
		{
			id: 'friday',
			text: t('t14'),
		},
		{
			id: 'saturday',
			text: t('t15'),
		},
		{
			id: 'sunday',
			text: t('t16'),
		},
	];

	return (
		<Framework
			description={location_variant?.seo_description}
			location={location}
			schema={{
				location: {
					address: location_data.address,
					city: location_data.city,
					image: getSrc(image_header),
					title: location_variant.title,
					zip_code: location_data.zip_code,
				},
			}}
			tags={location_variant.seo_keywords}
			title={location_variant.seo_title}
			tracking={{ content_group: 'Location' }}
			type="location"
		>
			<div className="container px-0 mt-lg-3 mt-2" id="content">
				<nav aria-label="breadcrumb">
					<Breadcrumb
						className=""
						links={[
							{
								base_url: '/mottagningar',
								id:
									location_data.groups && location_data.groups.length
										? location_data.groups[0]
										: null,
								type: 'group',
							},
							{ active: true, title: location_variant.title },
						]}
						location={true}
					/>
				</nav>
			</div>

			<div className="container">
				<div className="grid">
					<div className="grid-intro">
						<section id="clinic-title">
							<BookingBadge
								dropIn={
									location_data?.location_settings?.url_book ? false : true
								}
								status={location_data?.status}
							/>
							<PageHeader
								className="mt-2 pb-0 mb-0"
								excerpt={`${location_data.address}
								${`, `}
								${location_data.zip_code} ${location_data.city}`}
								pageVariant="nested_page"
								resetSpacing
								subTitle={location_variant.subtitle}
								title={location_variant.title}
							/>
						</section>
						{location_data?.location_settings?.url_book && (
							<div className="alert alert-success border-0 sm p-3 mt-3">
								<div className="row align-items-center">
									<div className="mt-3 col-md-6">
										<Trans i18nKey="t21" t={t} />
									</div>

									<div className="col-md-6">
										<Btn
											aria-controls="offcanvasRight"
											color="dark"
											component="button"
											data-bs-target="#bookingOffCanvas"
											data-bs-toggle="offcanvas"
											fullWidth
											size="md"
											type="button"
										>
											{t('t2')}
										</Btn>
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="grid-side">
						{image_header && (
							<GatsbyImage
								alt={location_variant.title}
								className="img-fluid mb-3"
								image={getImage(image_header)}
							/>
						)}

						{location_variant?.notice && (
							<div className="alert alert-warning border-0 sm p-3 mt-3">
								<div className="row align-items-center">
									<div className="col-md-6">
										<Trans defaults={location_variant?.notice} t={t} />
									</div>
								</div>
							</div>
						)}

						{!location_data?.location_settings?.url_book && (
							<div className="card border-0 bg-gray-100 rounded-0 mb-3">
								<div className="card-body p-3 small">
									<div className="d-flex align-items-center mb-3 gap-2">
										<ClockHistory />
										<h4 className="title-xsmall uppercase mb-0">{t('t1')}</h4>
									</div>

									{!location_data?.location_settings?.url_book &&
										opening_hours.map((item, index) => {
											const time =
												location_data?.location_settings[
													`opening_hours_${item.id}`
												] ?? t('t18');

											return (
												<div key={index}>
													{index > 0 && <hr className="my-1" />}
													<div className="row align-items-center">
														<div className="col">
															<p className="nowrap xs mb-0 text-dark">
																{item.text}
															</p>
														</div>

														<div className="col-auto">
															<span className="fw-600 nowrap xs my-0 text-dark">
																{time}
																{isSummerTime() &&
																	location_data?.location_settings[
																		`opening_hours_${item.id}`
																	] && <>*</>}
															</span>
														</div>
													</div>
												</div>
											);
										})}

									{isSummerTime() && <div className="mt-3 xs">{t('t24')}</div>}

									{!location_data?.location_settings?.url_book && (
										<div className="mt-3">
											{Object.entries(lunch_hours).map(
												([day, translation], index) => {
													const time =
														location_data?.location_settings[
															`lunch_hours_${day}`
														];
													if (!location_data?.location_settings || !time) {
														return;
													}

													return (
														<div
															className="alert alert-info border-0 p-2 rounded-0 mb-1"
															key={index}
														>
															<div className="row align-items-center">
																<div className="col">
																	<p className="nowrap xs mb-0 text-dark">
																		{translation}
																	</p>
																</div>

																<div className="col-auto">
																	<span className="fw-600 nowrap xs my-0 text-dark">
																		{time}
																	</span>
																</div>
															</div>
														</div>
													);
												},
											)}
										</div>
									)}
								</div>
							</div>
						)}

						<div className="card border-0 bg-gray-100 rounded-0 mb-3">
							<div className="card-body p-3 small">
								<div className="d-flex align-items-center mb-3 gap-2">
									<GeoAlt />
									<h4 className="title-xsmall uppercase mb-0">{t('t4')}</h4>
								</div>
								{/*
						<Link
									className="text-dark"
									onClick={(event) => {
										if (!location_data?.location_settings?.url_map)
											event.preventDefault();
									}}
									rel="noreferrer"
									target="_blank"
									to={location_data?.location_settings?.url_map}>
									 */}

								<p className="mb-0">
									{location_data.address}
									{`, `}
									{location_data.zip_code} {location_data.city}
								</p>
								{/* </Link> */}
							</div>
						</div>
						{(facility_parking || facility_wheelchair_access) && (
							<div className="card border-0 bg-gray-100 rounded-0 mb-3">
								<div className="card-body p-3 small">
									<div className="d-flex align-items-center mb-3 gap-2">
										<HouseGear />
										<h4 className="title-xsmall uppercase mb-0">{t('t6')}</h4>
									</div>
									<div className="row">
										<div className="col-12 pe-0">
											{facility_parking && (
												<>
													<StaticImage
														alt="Parking"
														className="location-icon"
														src="../images/framework/icons/icon-parking.png"
													/>
													<span className="small ms-1 text-muted">
														{t('t7')}
													</span>
												</>
											)}
										</div>
										<div className="col-12 pe-0">
											{facility_wheelchair_access && (
												<>
													<StaticImage
														alt="Wheelchair accessible"
														className="location-icon"
														src="../images/framework/icons/icon-disabled.png"
													/>
													<span className="x-small ms-1 text-muted">
														{t('t8')}
													</span>
												</>
											)}
										</div>
									</div>
								</div>
							</div>
						)}
						{(location_data.email || location_data.phone_number) && (
							<div className="card border-0 bg-gray-100 rounded-0 mb-3">
								<div className="card-body p-3 small">
									<div className="d-flex align-items-center mb-3 gap-2">
										<Telephone />
										<h4 className="title-xsmall uppercase">{t('t5')}</h4>
									</div>
									<p className="mb-0 small">
										<a
											className="text-dark"
											href={`tel:${location_data.phone_number}`}
										>
											{location_data.phone_number}
										</a>
									</p>
									<p className="mb-0 small">
										<a
											className="text-dark"
											href={`mailto:${location_data.email}`}
										>
											{location_data.email}
										</a>
									</p>
								</div>
							</div>
						)}
					</div>
					<div className="grid-content pt-5">
						<div className="article-content">
							<Trans defaults={location_variant.content} t={t} />
						</div>
						<div className="my-5">
							<h3 className="section-title">
								<Trans
									i18nKey="t25"
									t={t}
									values={{
										location_title: location_variant.title,
									}}
								/>
							</h3>
							<Offers color="light" variant="list" />
						</div>
						<section className="mb-4">
							<MapProvider>
								<LocationMap className="location-map-small" />
								<LocationMarkers location_ids={[location_data.location_id]} />
							</MapProvider>
						</section>
						<SectionHeader
							headingType="h2"
							title={t('t23')}
							to="/halsokontroller/"
						/>

						<Slider>
							<ProductList
								exclude_groups={[...siteMetadata.config.business_tests_groups]}
								grouping={false}
								include_product_types={['checkup']}
								minimum_markers={2}
								sort={{ key: 'position', order: 'desc' }}
								tracking={{
									item_list_name: 'Checkup products on index page',
									source: 'related',
								}}
								variant="card_small"
							/>
						</Slider>
					</div>
				</div>
			</div>

			<div
				aria-labelledby="offcanvasRightLabel"
				className="offcanvas offcanvas-end"
				id="bookingOffCanvas"
			>
				<div className="offcanvas-header">
					<h5 id="offcanvasRightLabel">{t('t19')}</h5>
					<button
						aria-label="Close"
						className="btn-close text-reset"
						data-bs-dismiss="offcanvas"
						type="button"
					></button>
				</div>
				<div className="offcanvas-body offcanvas-nav">
					<Trans i18nKey="t20" t={t} />

					<Link
						className="btn btn-primary btn-sm mt-2 rounded-2 text-light"
						rel="noreferrer"
						target="_blank"
						to={location_data?.location_settings?.url_book}
					>
						{t('t22')}
					</Link>
				</div>
			</div>
		</Framework>
	);
};

LocationItem.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

export default LocationItem;

export const pageQuery = graphql`
	query LocationById($id: String!) {
		newLocation(id: { eq: $id }) {
			address
			city
			email
			fields {
				files_header_url_src {
					childImageSharp {
						gatsbyImageData(layout: FULL_WIDTH)
					}
				}
			}
			files {
				symbol
				url
			}
			groups
			latitude
			location_id
			location_settings {
				facility_wheelchair_access
				opening_hours_monday
				opening_hours_tuesday
				opening_hours_wednesday
				opening_hours_thursday
				opening_hours_friday
				opening_hours_saturday
				opening_hours_sunday
				lunch_hours_monday
				lunch_hours_saturday
				url_book
				url_map
			}
			location_variants {
				content
				language {
					code
				}
				notice
				seo_description
				seo_keywords
				seo_title
				slug
				subtitle
				title
			}
			longitude
			phone_number
			zip_code
		}

		locales: allLocale {
			...translationFields
		}
	}
`;
